import { client, model } from '@whizz/api-client';
import { stringify } from 'query-string';
import React, { createContext, useCallback, useReducer, useEffect, useRef, useContext } from 'react';
import { v4 } from 'uuid';

const context = createContext({
    identity: null,
    logout: () => { },
    me: null,
    reauthorize: () => { },
    reidentify: () => { },
    resetIdleTimer: () => { },
    resync: () => { },
});

const reseed = Math.random;
const init = (initialResolution) => ({
    loaded: undefined !== initialResolution,
    loading: false,
    promiseFnArgs: [],
    rejection: undefined,
    resolution: initialResolution,
    seed: undefined,
});
const reducer = (state, action) => {
    switch (action.type) {
        case 'load':
            return Object.assign(Object.assign({}, state), { promiseFnArgs: action.payload, seed: reseed() });
        case 'loading':
            return Object.assign(Object.assign({}, state), { loaded: false, loading: true, rejection: undefined, resolution: undefined });
        case 'reload':
            return Object.assign(Object.assign({}, state), { seed: reseed() });
        case 'reject':
            return Object.assign(Object.assign({}, state), { loaded: true, loading: false, rejection: action.payload });
        case 'resolve':
            return Object.assign(Object.assign({}, state), { loaded: true, loading: false, resolution: action.payload });
        case 'reset':
            return init(action.payload);
        default:
            if ('production' !== process.env.NODE_ENV) {
                console.error('Invalid action provided to usePromiseLater()', action);
            }
            return state;
    }
};
const usePromise = (promiseFn, initialResolution) => {
    const [state, dispatch] = useReducer(reducer, initialResolution, init);
    const { loaded, loading, promiseFnArgs, rejection, resolution, seed, } = state;
    useEffect(() => {
        if (undefined === seed) {
            return;
        }
        dispatch({ type: 'loading' });
        let unmounted = false;
        promiseFn(...promiseFnArgs)
            .then((resolution) => {
            if (unmounted) {
                return;
            }
            dispatch({ type: 'resolve', payload: resolution });
        })
            .catch((rejection) => {
            if (unmounted) {
                return;
            }
            dispatch({ type: 'reject', payload: rejection });
        });
        return () => {
            unmounted = true;
        };
    }, [promiseFn, promiseFnArgs, seed]);
    return {
        load: useCallback((...promiseFnArgs) => {
            dispatch({ type: 'load', payload: promiseFnArgs });
        }, []),
        reload: useCallback(() => {
            dispatch({ type: 'reload' });
        }, []),
        reset: useCallback(() => {
            dispatch({ type: 'reset', payload: initialResolution });
        }, [initialResolution]),
        loaded: loaded,
        loading: loading,
        rejection: rejection,
        resolution: resolution,
    };
};

const Provider = context.Provider;
const identityFn = () => new Promise((resolve, reject) => {
    const loadTimer = setTimeout(() => {
        cleanup();
        reject(new Error('Load timeout'));
    }, 30000);
    let messageTimer;
    const iframe = window.document.createElement('iframe');
    const handleLoad = () => {
        clearTimeout(loadTimer);
        messageTimer = setTimeout(() => {
            cleanup();
            reject(new Error('Message timeout'));
        }, 10000);
    };
    const handleMessage = (event) => {
        if (`https://${process.env.REACT_APP_HOSTNAME_LOGIN}` === event.origin
            && 'object' === typeof event.data
            && 'identity' in event.data) {
            cleanup();
            resolve(event.data.identity ? new model.Identity(JSON.parse(event.data.identity)) : null);
        }
    };
    const cleanup = () => {
        clearTimeout(loadTimer);
        clearTimeout(messageTimer);
        window.removeEventListener('message', handleMessage);
        iframe.removeEventListener('load', handleLoad);
        // Cannot remove the iframe because this results in an race condition/infinite loop within the
        // getActiveElementDeep() function of react-dom (only observable in IE <= 11 and Edge <= 18)
        // window.document.body.removeChild(iframe);
    };
    window.addEventListener('message', handleMessage);
    iframe.addEventListener('load', handleLoad);
    iframe.style.display = 'none';
    iframe.setAttribute('src', `https://${process.env.REACT_APP_HOSTNAME_LOGIN}/identity?_cb=${v4()}`);
    window.document.body.appendChild(iframe);
});
const meFn = (identity) => {
    if (!identity) {
        return Promise.resolve(null);
    }
    switch (identity.user.type) {
        case 'organization_contact':
            return model.OrganizationContact.one(identity.user.id);
        case 'parent':
            return model.Parent.one(identity.user.id);
        case 'student':
            return model.Student.one(identity.user.id);
        case 'teacher':
            return model.Teacher.one(identity.user.id);
        default:
            return Promise.resolve(null);
    }
};
const trackFn = (me, mixpanel = null) => {
    return new Promise((resolve, reject) => {
        if (!mixpanel) {
            resolve();
            return;
        }
        mixpanel.people.set({
            $email: me.email,
            id: me.id,
            first_name: me.first_name,
            last_name: me.last_name,
            username: me.username,
            type: me.type,
        }, (err) => {
            if (err) {
                reject(err);
                return;
            }
            resolve();
        });
    });
};
const Loading = () => null;
const defaultAuthorizeFn = (identity, me) => Promise.resolve();
const defaultLoadingComponent = React.createElement(Loading, null);
const defaultLogoutParams = {};
const IdentityProvider = ({ authorizer: authorizeFn = defaultAuthorizeFn, children, idleTimeout, loadingComponent = defaultLoadingComponent, logoutParams = defaultLogoutParams, mixpanel, luxon, }) => {
    const { load: identityLoad, loaded: identityLoaded, rejection: identityError, reload: identityReload, resolution: identity, } = usePromise(identityFn);
    const { load: meLoad, loaded: meLoaded, rejection: meError, reload: meReload, resolution: me, } = usePromise(meFn);
    const { load: authorizeLoad, loaded: authorizeLoaded, rejection: authorizeError, } = usePromise(authorizeFn);
    const { load: trackLoad, loaded: trackLoaded, } = usePromise(trackFn);
    const logout = useCallback((params = {}) => ((identity ? identity.destroy() : Promise.resolve())
        .catch(() => { })
        .finally(() => {
        params = Object.assign(Object.assign({}, logoutParams), params);
        if (identity && !('locale' in params)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            params.locale = (identity.user.locale_preferred || identity.user.locale).id;
        }
        const query = stringify(params);
        window.location.assign(`https://${process.env.REACT_APP_HOSTNAME_LOGIN}/${query && `?${query}`}`);
    })), [identity, logoutParams]);
    const idleTimer = useRef();
    const startIdleTimer = useCallback(() => {
        if (idleTimeout) {
            idleTimer.current = setTimeout(() => logout({ error: 'expired' }), idleTimeout * 1000);
        }
    }, [idleTimeout, logout]);
    const stopIdleTimer = useCallback(() => {
        if (idleTimer.current) {
            clearTimeout(idleTimer.current);
            idleTimer.current = undefined;
        }
    }, []);
    const resetIdleTimer = useCallback(() => {
        stopIdleTimer();
        startIdleTimer();
    }, [startIdleTimer, stopIdleTimer]);
    useEffect(() => {
        if (identityLoaded) {
            if (identityError) {
                setTimeout(identityReload, 1000);
            }
            return;
        }
        identityLoad();
    }, [identityError, identityLoad, identityLoaded, identityReload]);
    useEffect(() => {
        if (!identityLoaded || identityError || meLoaded) {
            if (meError) {
                setTimeout(meReload, 1000);
            }
            return;
        }
        meLoad(identity);
    }, [identity, identityError, identityLoaded, meError, meLoad, meLoaded, meReload]);
    useEffect(() => {
        if (!meLoaded || meError || authorizeLoaded) {
            return;
        }
        authorizeLoad(identity, me);
    }, [authorizeLoad, authorizeLoaded, identity, me, meError, meLoaded]);
    useEffect(() => {
        if (!authorizeLoaded || authorizeError || trackLoaded) {
            return;
        }
        trackLoad(me, mixpanel);
    }, [authorizeError, authorizeLoaded, me, mixpanel, trackLoad, trackLoaded]);
    useEffect(() => {
        if (!identity) {
            return;
        }
        startIdleTimer();
        const interceptorId = client.http().interceptors.request.use((request) => {
            resetIdleTimer();
            return Object.assign(Object.assign({}, request), { headers: Object.assign(Object.assign({}, request.headers), { authorization: `Bearer ${identity.token}` }) });
        });
        if (mixpanel && mixpanel.config.token) {
            mixpanel.identify(identity.claims.sub);
        }
        return () => {
            stopIdleTimer();
            client.http().interceptors.request.eject(interceptorId);
            if (mixpanel && mixpanel.config.token) {
                mixpanel.reset();
            }
        };
    }, [identity, mixpanel, resetIdleTimer, startIdleTimer, stopIdleTimer]);
    useEffect(() => {
        if (!me || !luxon || luxon.Settings.defaultZoneName === me.timezone) {
            return;
        }
        const initialZoneName = luxon.Settings.defaultZoneName;
        luxon.Settings.defaultZoneName = me.timezone;
        return () => {
            luxon.Settings.defaultZoneName = initialZoneName;
        };
    }, [luxon, me]);
    if (!identityLoaded || !meLoaded || !authorizeLoaded || !trackLoaded || authorizeError) {
        if (authorizeError) {
            logout({
                error: identity ? 'noaccess' : undefined,
                referrer: !identity ? window.location.href : undefined,
            });
        }
        return loadingComponent;
    }
    return (React.createElement(Provider, { value: {
            identity,
            logout,
            me,
            resetIdleTimer,
            reload: meReload,
        } }, children));
};

const useIdentity = () => useContext(context);

const Consumer = context.Consumer;
const withIdentity = (Component) => (props) => (React.createElement(Consumer, null, (value) => React.createElement(Component, Object.assign({}, props, value))));

export { IdentityProvider, useIdentity, withIdentity };
